// react
import { MouseEvent } from 'react';

// types
import { Severity } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// mui
import {
	Box,
	Chip,
	Grid,
	Paper,
	PaperProps,
	SxProps,
	Typography,
	TypographyProps,
} from '@mui/material';

// ui
import { ChipStatus, ChipStatusProps } from '../../ui/ChipStatus/ChipStatus';
import { Empty } from '../../ui/Empty/Empty';
import { IconCircle, IconCircleProps } from '../../ui/IconCircle/IconCircle';

// dialogs
import {
	DialogUtilization,
	DialogUtilizationProps,
} from '../../dialogs/DialogUtilization/DialogUtilization';
import { theme } from '../../../theme';
import {
	DialogTraceability,
	DialogTraceabilityProps,
} from '../../dialogs/DialogTraceability/DialogTraceability';

// theme
import { colorsSeverity } from '../../../theme';

// props
export interface CardSeverityProps {
	ChipStatusProps?: ChipStatusProps;
	description?: string;
	dialog?: 'TRACEABILITY' | 'UTILIZATION';
	DialogProps?: Partial<DialogUtilizationProps> | Partial<DialogTraceabilityProps>;
	footnote?: string;
	hasBoxShadow?: boolean;
	hasChipStatus?: boolean;
	hasLineItemDividers?: boolean;
	isActive?: boolean;
	isLoading?: boolean;
	label: TypographyProps;
	lineItems?: {
		label: string;
		value: string | number;
	}[];
	spacing?: number;
	severity: null | Severity;
	value: TypographyProps;
}

export const CardSeverity = ({
	ChipStatusProps = {
		status: 'PENDING',
	},
	description,
	dialog,
	DialogProps,
	footnote,
	hasBoxShadow = true,
	hasChipStatus = true,
	hasLineItemDividers = false,
	isActive = true,
	isLoading = false,
	lineItems = [],
	severity,
	spacing = 3,
	label,
	value,
}: CardSeverityProps) => {
	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialog = searchParams.get('dialog');

	// vars
	const dialogId = DialogProps?.id || dialog;

	const handleOpenDialog = (e: MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
		e.stopPropagation();

		if (dialogId) {
			searchParams.set('dialog', dialogId);
			setSearchParams(searchParams);
		}
	};

	const handleCloseDialog = () => {
		searchParams.delete('dialog');
		searchParams.delete('dialogTab');
		setSearchParams(searchParams);
	};

	const PaperProps: PaperProps = {};

	const hasDialog = dialog && isActive;

	if (hasDialog) {
		PaperProps.onClick = handleOpenDialog;
	}

	const colorSeverity = isActive ? severity : 'default';
	const borderColor = colorsSeverity[colorSeverity || 'default'].border;
	const borderStyle = 'solid';
	const borderWidth = 2;

	let justifyContent = 'flex-start';
	let spacingBottom = spacing;

	if (lineItems.length > 0 || footnote) {
		justifyContent = 'space-between';
		spacingBottom = 0;
	}

	let content = null;

	if (isLoading) {
		content = (
			<Empty hasContent={false} hasTitle={false} isLoading={isLoading} position="absolute" />
		);
	} else {
		content = (
			<Grid
				container
				direction="column"
				justifyContent={justifyContent}
				sx={{ gap: 0, height: `100%` }}>
				{isActive ? (
					<>
						<Grid item>
							{dialog && (
								<Grid container direction="column">
									<Grid item>
										<Box
											sx={{
												padding: {
													xs: theme.spacing(2),
													md: theme.spacing(spacing, spacing, 2, spacing),
												},
											}}>
											<Grid
												container
												alignItems="center"
												justifyContent={dialog ? 'space-between' : 'flex-end'}>
												<Grid item>
													<IconCircle
														color={severity as IconCircleProps['color']}
														scheme="dark"
														size="xs"
														type="help"
														variant="outlined"
													/>
												</Grid>
											</Grid>
										</Box>
									</Grid>
								</Grid>
							)}
							<Grid item>
								<Box
									sx={{
										padding: {
											xs: theme.spacing(spacing),
											md: theme.spacing(spacing, spacing, spacingBottom, spacing),
										},
									}}>
									<Grid alignItems="center" container justifyContent="space-between" spacing={2}>
										<Grid item>
											<Typography variant="display" {...value} />
										</Grid>
										{hasChipStatus && (
											<Grid item>
												<ChipStatus {...ChipStatusProps} />
											</Grid>
										)}
									</Grid>
									<Typography variant="h3" {...label} />
									{description && (
										<Grid alignItems="flex-end" container spacing={1}>
											<Grid item flex={1}>
												<Typography
													color={colorsSeverity[colorSeverity || 'default'].secondary}
													variant="caption">
													{description}
												</Typography>
											</Grid>
										</Grid>
									)}
								</Box>
							</Grid>

							{lineItems?.length > 0 && (
								<Grid item>
									<Grid container direction="column" justifyItems="flex-end">
										{lineItems?.map((item, i) => {
											const isFirstLineItem = i === 0;
											const isLastLineItem = i === lineItems.length - 1;

											let paddingBottomXs = 0;
											let paddingBottomMd = 0;

											let paddingTopXs = 0;
											let paddingTopMd = 0;

											if (isFirstLineItem) {
												paddingTopXs = 2;
												paddingTopMd = 2;
											}

											if (isLastLineItem) {
												paddingBottomXs = 2;
												paddingBottomMd = 3;
											}

											return (
												<Grid item>
													<Box
														sx={() => {
															let sx: SxProps = {
																padding: {
																	xs: theme.spacing(paddingTopXs, 2, paddingBottomXs, 2),
																	md: theme.spacing(paddingTopMd, 3, paddingBottomMd, 3),
																},
															};

															if (hasLineItemDividers && !isLastLineItem) {
																sx = {
																	...sx,
																	borderBottomColor: borderColor,
																	borderBottomStyle: borderStyle,
																	borderBottomWidth: borderWidth,
																};
															}

															return sx;
														}}>
														<Grid container spacing={1}>
															<Grid item>
																<Typography component="span" fontWeight={600} variant="caption">
																	{item.value}
																</Typography>
															</Grid>
															<Grid item>
																<Typography component="span" variant="caption">
																	{item.label}
																</Typography>
															</Grid>
														</Grid>
													</Box>
												</Grid>
											);
										})}
									</Grid>
								</Grid>
							)}
						</Grid>

						{footnote && (
							<Grid item>
								<Box
									sx={() => {
										let spacingTop = 0;

										if (lineItems.length > 0) {
											spacingTop = spacing;
										}

										return {
											padding: {
												xs: theme.spacing(spacingTop, spacing, spacing, spacing),
											},
										};
									}}>
									<Typography sx={{ opacity: 0.6 }} variant="caption">
										{footnote}
									</Typography>
								</Box>
							</Grid>
						)}
					</>
				) : (
					<Grid item sx={{ height: '100%' }}>
						<Box
							sx={{
								position: 'absolute',
								top: '50%',
								left: '50%',
								transform: 'translate(-50%, -50%)',
							}}>
							<Grid container alignItems="center" direction="column" spacing={1}>
								<Grid item>
									<Chip color="default" label={t('comingSoon')} size="small" variant="twoTone" />
								</Grid>
								<Grid item>
									<Typography sx={{ textAlign: 'center' }}>
										{t('comingSoon.description')}
									</Typography>
								</Grid>
							</Grid>
						</Box>
					</Grid>
				)}
			</Grid>
		);
	}

	return (
		<>
			<Paper
				component={hasDialog ? 'button' : 'div'}
				sx={(theme) => {
					const sx: SxProps = {
						color: colorsSeverity[colorSeverity || 'default'].primary,
						backgroundColor: theme.palette.common.white,
						borderStyle,
						borderColor,
						borderWidth,
						position: 'relative',
						width: '100%',
						height: '100%',
						textAlign: 'left',
					};

					if (severity && colorSeverity) {
						sx.backgroundColor = colorsSeverity[colorSeverity].background;
					}

					if (!hasBoxShadow) {
						sx.boxShadow = 'none';
					}

					return sx;
				}}
				{...PaperProps}>
				{content}
			</Paper>
			{dialog === 'TRACEABILITY' && (
				<DialogTraceability
					{...(DialogProps as DialogTraceabilityProps)}
					isOpen={paramDialog === dialogId}
					onClose={handleCloseDialog}
				/>
			)}
			{dialog === 'UTILIZATION' && (
				<DialogUtilization
					{...(DialogProps as DialogUtilizationProps)}
					isOpen={paramDialog === dialogId}
					onClose={handleCloseDialog}
				/>
			)}
		</>
	);
};
